import React, { useState, useEffect, useRef } from 'react';
import { HiCheck } from "react-icons/hi";
import { SlPencil, SlTrash } from "react-icons/sl";
import './App-dashboard.css';
import { SlPlus, SlLogout, SlClose, SlChart } from "react-icons/sl";
import { getUsers, createUser, updateUser, deleteUser, getDashboardStats, getDashboardCosts, getBuyerStats } from './api'; // Import API functions
import Select from 'react-select'; // Ensure this is imported at the top of your file
import { useNavigate } from 'react-router-dom'; // Import useNavigate for redirection
import Map from './MapChart';
import { FaMapMarkedAlt, FaList } from "react-icons/fa"; // Add these imports
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { ThreeDots, SpinningCircles } from 'react-loading-icons';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Dashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [activeStatsTab, setActiveStatsTab] = useState('projects');
  const [activeCountriesView, setActiveCountriesView] = useState('map');
  const [users, setUsers] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const containerRef = useRef(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [buyerStats, setBuyerStats] = useState(null);
  const [buyerStatsLoading, setBuyerStatsLoading] = useState(false);
  const [showBuyerModal, setShowBuyerModal] = useState(false);
  const [stats, setStats] = useState({
    totalProjects: 0,
    totalUsers: 0,
    activeUsers: 0,
    monthlyProjects: Array(12).fill(0),
    monthlyCosts: Array(12).fill(0), // Add monthly costs
    totalCost: 0, // Add total cost
    activeBuyers: [],
    countryData: [],
    categoryData: []
  });
  const listViewRef = useRef(null); // Add ref for list view
  const [showPassword, setShowPassword] = useState(false); // Add this state
  const [statsCache, setStatsCache] = useState({});
  const latestYearRef = useRef(selectedYear);

  const roleOptions = [
    { value: 'Admin', label: 'Admin' },
    { value: 'User', label: 'User' },
    { value: 'Limited User', label: 'Limited User' }

  ];
  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: '#333333',
      color: '#e0e0e0',
      border: '1px solid #757575',
      borderRadius: '4px',
      height: '4vw',
      minHeight: '30px', // Adjust based on your design needs
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#ff616d',
      },
      zIndex: 9999,
    }),
    input: (provided) => ({
      ...provided,
      color: '#e0e0e0',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#e0e0e0',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#333333',
      color: '#e0e0e0',
      border: 'none',
      zIndex: 9999,
      overflow: 'visible'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#424242' : '#333333',
      color: '#e0e0e0',
      '&:hover': {
        backgroundColor: '#424242',
      },
      zIndex: 9999,
      overflow: 'visible'
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };
  // Function to find the role option based on the current role value
  const getRoleOption = (role) => roleOptions.find(option => option.value === role);
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [users.length]); // Triggered whenever the users array changes length
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        console.log('Fetching users...');
        const fetchedUsers = await getUsers();
        console.log('Fetched users:', fetchedUsers);
        // Sort users by ID in ascending order
        const sortedUsers = fetchedUsers.sort((a, b) => a.id - b.id);
        const usersWithFlag = sortedUsers.map(user => ({ ...user, isNew: false }));
        setUsers(usersWithFlag);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  // Move the fetchStats function outside useEffect
  const fetchStats = async (year) => {
    latestYearRef.current = year;
    // Check cache first
    if (statsCache[year]) {
      setStats(statsCache[year]);
      setStatsLoading(false);
      // Still fetch costs in case they are not cached or may have changed
      fetchCosts(year, statsCache[year]);
      return;
    }
    setStatsLoading(true);
    try {
      const statsData = await getDashboardStats(year);
      if (latestYearRef.current !== year) return; // Only update if still latest
      setStats(statsData);
      setStatsCache(prev => ({ ...prev, [year]: statsData })); // Save to cache
      // Fetch costs separately
      fetchCosts(year, statsData);
    } catch (error) {
      if (latestYearRef.current !== year) return;
      setStats({
        totalProjects: 0,
        totalUsers: 0,
        activeUsers: 0,
        monthlyProjects: Array(12).fill(0),
        monthlyCosts: Array(12).fill(0),
        totalCost: 0,
        activeBuyers: [],
        countryData: [],
        categoryData: []
      });
    } finally {
      if (latestYearRef.current === year) setStatsLoading(false);
    }
  };

  const fetchCosts = async (year, baseStats) => {
    try {
      const costsData = await getDashboardCosts(year);
      if (latestYearRef.current !== year) return;
      setStats(prev => ({ ...prev, ...costsData }));
      setStatsCache(prev => ({ ...prev, [year]: { ...baseStats, ...costsData } }));
    } catch (error) {
      // Optionally handle error
    }
  };

  // Add useEffect to fetch stats when stats tab becomes active
  useEffect(() => {
    fetchStats(selectedYear);
    // eslint-disable-next-line
  }, [selectedYear]);

  const handleLogout = () => {
    // Implement logout logic here, e.g., clearing user data
    navigate('/'); // Redirect to login page
  };
  const handleAddUser = () => {
    const newUser = {
      id: users.length + 1,
      name: newName,
      password: newPassword,
      email: newEmail,
      role: newRole,
      isNew: true // Mark as new user

    };
    setUsers([...users, newUser]);
    setEditingIndex(users.length); // Set editing index to new user
    setNewName('');
    setNewPassword('');
    setNewEmail('');
    setNewRole('');
  };

  const handleEditUser = (index) => {
    setEditingIndex(index);
    const user = users[index];
    setNewName(user.name);
    setNewPassword(''); // Always blank when editing
    setNewEmail(user.email);
    setNewRole(user.role);
    setShowPassword(false);
  };

  const handleConfirmEdit = async () => {
    if (!newName || !newEmail || !newRole) {
      alert('Name, email, and role must be filled out.');
      return;
    }

    const user = {
      name: newName,
      email: newEmail,
      role: newRole
    };
    if (newPassword) user.password = newPassword; // Only send if not blank

    let updatedUsers = [...users];
    const isCurrentUserNew = users[editingIndex].isNew;

    if (isCurrentUserNew) {
      // Create new user
      const newUser = await createUser({ ...user, password: newPassword });
      newUser.isNew = false; // Update isNew flag
      updatedUsers[editingIndex] = newUser; // Replace temporary user data with real data from backend
    } else {
      // Update existing user
      const updatedUserData = await updateUser(users[editingIndex].id, user);
      updatedUsers[editingIndex] = updatedUserData;
    }

    setUsers(updatedUsers);
    setEditingIndex(null);
    setNewName('');
    setNewPassword('');
    setNewEmail('');
    setNewRole('');
  };

  const handleCancelEdit = () => {
    // Reset the form fields if necessary
    setNewName('');
    setNewPassword('');
    setNewEmail('');
    setNewRole('');
    // Exit edit mode
    setEditingIndex(null);
    // Remove the row if it was a new user
    if (users[editingIndex].isNew) {
      setUsers(users.filter((_, index) => index !== editingIndex));
    }
  };

  const handleRemoveUser = async (index) => {
    if (window.confirm("Are you sure you want to remove this user?")) {
      await deleteUser(users[index].id);
      setUsers(users.filter((_, i) => i !== index));
    }
  };

  const handleBuyerClick = async (buyer) => {
    setSelectedBuyer(buyer);
    setShowBuyerModal(true);
    setBuyerStatsLoading(true);

    try {
      const buyerData = await getBuyerStats(buyer.name, selectedYear);
      setBuyerStats(buyerData);
    } catch (error) {
      console.error('Error fetching buyer statistics:', error);
      setBuyerStats({
        name: buyer.name,
        monthlyProjects: Array(12).fill(0),
        totalProjects: 0
      });
    } finally {
      setBuyerStatsLoading(false);
    }
  };

  const closeBuyerModal = () => {
    setShowBuyerModal(false);
    setSelectedBuyer(null);
    setBuyerStats(null);
  };

  const renderUsersTab = () => (
    <div className="projects-div2">
      <div className="users-container">
        <h2>User Management</h2>
        <div ref={containerRef} className="users-table">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Password</th>
                <th>Email</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <tr key={user.id}>
                  <td>{editingIndex === index ? (
                    <input
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                    />
                  ) : user.name}</td>
                  <td>{editingIndex === index ? (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type={showPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: '#ff616d' }}
                        title={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? "🙈" : "👁️"}
                      </button>
                    </div>
                  ) : '••••••••'}</td>
                  <td>{editingIndex === index ? (
                    <input
                      className="email-input"
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                    />
                  ) : user.email}</td>
                  <td>{editingIndex === index ? (
                    <Select
                      styles={customSelectStyles}
                      options={roleOptions}
                      onChange={(selectedOption) => setNewRole(selectedOption.value)}
                      value={getRoleOption(newRole)}
                      menuPortalTarget={document.body}
                    />
                  ) : user.role}</td>
                  <td>
                    {editingIndex === index ? (<>
                      <button className="confirm-button" onClick={handleConfirmEdit}><HiCheck /></button>
                      <button className="delete-button" onClick={handleCancelEdit}><SlClose /></button>
                    </>) : (
                      <>
                        <button className="edit-button" onClick={() => handleEditUser(index)}><SlPencil /></button>
                        <button className="delete-button" onClick={() => handleRemoveUser(index)}><SlTrash /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan="6" className="sticky-row">
                  <button className="add-user-button" onClick={handleAddUser}><SlPlus /> ADD NEW USER</button>
                </td>
              </tr>
            </tbody>
          </table>
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '15px' }}>
              <ThreeDots />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStatsTab = () => (
    <div className="projects-div2">
      <div className="stats-container">
        <div className="stats-tabs">
          <button
            className={`stats-tab-button ${activeStatsTab === 'projects' ? 'active' : ''}`}
            onClick={() => setActiveStatsTab('projects')}
          >
            Projects
          </button>
          <button
            className={`stats-tab-button ${activeStatsTab === 'buyers' ? 'active' : ''}`}
            onClick={() => setActiveStatsTab('buyers')}
          >
            Buyers
          </button>
          <button
            className={`stats-tab-button ${activeStatsTab === 'countries' ? 'active' : ''}`}
            onClick={() => setActiveStatsTab('countries')}
          >
            Countries
          </button>
          <button
            className={`stats-tab-button ${activeStatsTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveStatsTab('categories')}
          >
            Categories
          </button>
        </div>
        <div className="stats-grid" style={{ position: 'relative', minHeight: 200 }}>
          {activeStatsTab === 'projects' && (
            <>
              <div className="projects-layout" style={{ position: 'relative' }}>
                <div className="buyers-stats">
                  <div className="stat-card">
                    <h3>Total Projects</h3>
                    <p className="stat-number">{stats.totalProjects}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Projects in {selectedYear}</h3>
                    <p className="stat-number">{stats.monthlyProjects.reduce((sum, n) => sum + n, 0)}</p>
                  </div>
                  <div className="stat-card">
                    <h3>OpenAI Expenses ({selectedYear})</h3>
                    <div style={{ position: 'relative', minHeight: 32 }}>

                      <p className="stat-number" style={{ color: '#ff616d', margin: 0 }}>
                        {typeof stats.totalCost === 'number' ? `$${stats.totalCost.toFixed(2)}` : '...'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="stat-card full-width" style={{ position: 'relative' }}>
                  <h3>Completed Projects</h3>
                  <div className="chart-container" style={{ position: 'relative', minHeight: 240 }}>

                    {((typeof stats.totalCost !== 'number' || !Array.isArray(stats.monthlyCosts)) && !statsLoading) && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 19,
                        pointerEvents: 'none',
                        background: 'rgba(255,255,255,0.10)',
                        transition: 'background 0.2s',
                      }}>
                        <SpinningCircles fill="#ff616d" height="48px" width="48px" />
                      </div>
                    )}
                    <Bar
                      data={{
                        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                        datasets: [{
                          label: 'Completed Projects',
                          data: stats.monthlyProjects,
                          backgroundColor: 'rgba(255, 99, 132, 0.7)',
                          borderColor: 'rgba(255, 99, 132, 1)',
                          borderWidth: 2,
                          borderRadius: 8,
                          barThickness: 'flex',
                          maxBarThickness: 40,
                          hoverBackgroundColor: 'rgba(255, 99, 132, 0.9)',
                          hoverBorderColor: 'rgba(255, 99, 132, 1)',
                          hoverBorderWidth: 3
                        }]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        animation: {
                          duration: 1500,
                          easing: 'easeInOutQuart'
                        },
                        layout: {
                          padding: {
                            top: 20,
                            right: 20,
                            bottom: 20,
                            left: 20
                          }
                        },
                        plugins: {
                          legend: {
                            display: false
                          },
                          tooltip: {
                            backgroundColor: 'rgba(0, 0, 0, 0.85)',
                            titleColor: '#fff',
                            bodyColor: '#fff',
                            padding: 12,
                            cornerRadius: 6,
                            displayColors: false,
                            titleFont: {
                              size: 14,
                              weight: 'bold'
                            },
                            bodyFont: {
                              size: 13
                            },
                            callbacks: {
                              title: (context) => {
                                const month = context[0].label;
                                return `${month}`;
                              },
                              label: (context) => {
                                const idx = context.dataIndex;
                                const projects = context.raw;
                                const cost = stats.monthlyCosts && typeof stats.monthlyCosts[idx] === 'number' ? stats.monthlyCosts[idx] : null;
                                return [
                                  `${projects} Projects`,
                                  `OpenAI Cost: $${cost !== null ? cost.toFixed(2) : '...'}`
                                ];
                              }
                            }
                          }
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            grid: {
                              color: 'rgba(255, 255, 255, 0.1)',
                              drawBorder: false,
                              drawTicks: false
                            },
                            ticks: {
                              color: '#e0e0e0',
                              stepSize: 1,
                              font: {
                                size: 12,
                                weight: 'bold'
                              },
                              padding: 8,
                              maxRotation: 0
                            }
                          },
                          x: {
                            grid: {
                              display: false
                            },
                            ticks: {
                              color: '#e0e0e0',
                              font: {
                                size: 12,
                                weight: 'bold'
                              },
                              padding: 8,
                              maxRotation: 0
                            }
                          }
                        },
                        interaction: {
                          intersect: false,
                          mode: 'index'
                        }
                      }}
                    />
                  </div>
                  <div className="year-navigation">
                    <button onClick={() => setSelectedYear(prev => prev - 1)}>&lt;</button>
                    <span>{selectedYear}</span>
                    <button onClick={() => setSelectedYear(prev => prev + 1)}>&gt;</button>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeStatsTab === 'buyers' && (
            <>
              <div className="projects-layout">
                <div className="buyers-stats">
                  <div className="stat-card">
                    <h3>Total Users</h3>
                    <p className="stat-number">{stats.totalUsers}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Active Users</h3>
                    <p className="stat-number">{stats.activeUsers}</p>
                  </div>
                </div>
                <div className="stat-card full-width">
                  <h3>Buyer Activity</h3>
                  <div className="buyers-table">
                    <table className="elegant-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Projects Completed</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.activeBuyers.map((buyer, index) => (
                          <tr key={index} onClick={() => handleBuyerClick(buyer)} className="clickable">
                            <td>{buyer.name}</td>
                            <td>{buyer.projectsCompleted}</td>
                            <td>
                              <span className={`status-badge ${buyer.isActive ? 'active' : 'inactive'}`}>
                                {buyer.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeStatsTab === 'countries' && (
            <>
              <div className="stats-grid">
                <div className="stat-card full-width">
                  <div className="map-header">
                    <h3>Country Repartition</h3>
                    <div className="view-toggle">
                      <button
                        className={`view-toggle-button ${activeCountriesView === 'map' ? 'active' : ''}`}
                        onClick={() => {
                          if (listViewRef.current) listViewRef.current.scrollTop = 0; // Scroll list to top before switching
                          setActiveCountriesView('map');
                        }}
                        title="Map View"
                      >
                        <FaMapMarkedAlt />
                      </button>
                      <button
                        className={`view-toggle-button ${activeCountriesView === 'list' ? 'active' : ''}`}
                        onClick={() => setActiveCountriesView('list')}
                        title="List View"
                      >
                        <FaList />
                      </button>
                    </div>
                  </div>
                  {activeCountriesView === 'map' ? (
                    <div className="map-container">
                      <Map />
                    </div>
                  ) : (
                    <div className="list-view" ref={listViewRef}>
                      {(() => {
                        return (
                          <table className="elegant-table">
                            <thead>
                              <tr>
                                <th>Country</th>
                                <th>Projects</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(stats.countryData && stats.countryData.length > 0) && stats.countryData.map((country, index) => (
                                <tr key={index}>
                                  <td>{country.name}</td>
                                  <td>{country.count}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {activeStatsTab === 'categories' && (
            <>
              <div className="stats-grid">
                <div className="stat-card full-width">
                  <h3>Sourced Categories</h3>
                  <div className="list-view">
                    <table className="elegant-table">
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Projects</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(stats.categoryData && stats.categoryData.length > 0) && stats.categoryData.map((category, index) => (
                          <tr key={index}>
                            <td>{category.name}</td>
                            <td>{category.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="App">
        <div className="header">
          <h1><span className="titleLetters">D</span>ragon<span className="titleLetters">S</span>couter</h1>
        </div>
        <div className="admin-dashboard">
          <div className="dashboard-tabs">
            <button
              className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              Users
            </button>
            <button
              className={`tab-button ${activeTab === 'stats' ? 'active' : ''}`}
              onClick={() => setActiveTab('stats')}
            >
              Stats
            </button>
            <button className="logout-button" onClick={handleLogout}>
              <SlLogout /> Logout
            </button>
          </div>
          {activeTab === 'users' && renderUsersTab()}
          {activeTab === 'stats' && renderStatsTab()}
        </div>
      </div>
      {showBuyerModal && (
        <div className="buyer-modal" onClick={closeBuyerModal}>
          <div className="buyer-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedBuyer?.name} - Project Statistics</h2>
              <span className="close-button" onClick={closeBuyerModal}>&times;</span>
            </div>
            {buyerStatsLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '15px' }}>
                <ThreeDots />
              </div>
            ) : buyerStats && (
              <>
                <div className="buyer-stats-summary">
                  <div className="stat-card">
                    <h3>Total Projects</h3>
                    <p className="stat-number">{buyerStats.totalProjects}</p>
                  </div>
                  <div className="stat-card">
                    <h3>Projects in {selectedYear}</h3>
                    <p className="stat-number">{buyerStats.monthlyProjects.reduce((sum, n) => sum + n, 0)}</p>
                  </div>
                </div>
                <div className="stat-card full-width">
                  <h3>Monthly Projects - {selectedYear}</h3>
                  <div className="chart-container">
                    <Bar
                      data={{
                        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                        datasets: [{
                          label: 'Completed Projects',
                          data: buyerStats.monthlyProjects,
                          backgroundColor: 'rgba(255, 99, 132, 0.7)',
                          borderColor: 'rgba(255, 99, 132, 1)',
                          borderWidth: 2,
                          borderRadius: 8,
                          barThickness: 'flex',
                          maxBarThickness: 40,
                          hoverBackgroundColor: 'rgba(255, 99, 132, 0.9)',
                          hoverBorderColor: 'rgba(255, 99, 132, 1)',
                          hoverBorderWidth: 3
                        }]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        animation: {
                          duration: 1500,
                          easing: 'easeInOutQuart'
                        },
                        layout: {
                          padding: {
                            top: 20,
                            right: 20,
                            bottom: 20,
                            left: 20
                          }
                        },
                        plugins: {
                          legend: {
                            display: false
                          },
                          tooltip: {
                            backgroundColor: 'rgba(0, 0, 0, 0.85)',
                            titleColor: '#fff',
                            bodyColor: '#fff',
                            padding: 12,
                            cornerRadius: 6,
                            displayColors: false,
                            titleFont: {
                              size: 14,
                              weight: 'bold'
                            },
                            bodyFont: {
                              size: 13
                            },
                            callbacks: {
                              title: () => '',
                              label: (context) => `${context.raw} Projects`
                            }
                          }
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            grid: {
                              color: 'rgba(255, 255, 255, 0.1)',
                              drawBorder: false,
                              drawTicks: false
                            },
                            ticks: {
                              color: '#e0e0e0',
                              stepSize: 1,
                              font: {
                                size: 12,
                                weight: 'bold'
                              },
                              padding: 8,
                              maxRotation: 0
                            }
                          },
                          x: {
                            grid: {
                              display: false
                            },
                            ticks: {
                              color: '#e0e0e0',
                              font: {
                                size: 12,
                                weight: 'bold'
                              },
                              padding: 8,
                              maxRotation: 0
                            }
                          }
                        },
                        interaction: {
                          intersect: false,
                          mode: 'index'
                        }
                      }}
                    />
                  </div>
                  <div className="year-navigation">
                    <button onClick={async () => {
                      const newYear = selectedYear - 1;
                      setSelectedYear(newYear);
                      setBuyerStatsLoading(true);
                      try {
                        const buyerData = await getBuyerStats(selectedBuyer.name, newYear);
                        setBuyerStats(buyerData);
                      } catch (error) {
                        console.error('Error fetching buyer statistics:', error);
                      } finally {
                        setBuyerStatsLoading(false);
                      }
                    }}>&lt;</button>
                    <span>{selectedYear}</span>
                    <button onClick={async () => {
                      const newYear = selectedYear + 1;
                      setSelectedYear(newYear);
                      setBuyerStatsLoading(true);
                      try {
                        const buyerData = await getBuyerStats(selectedBuyer.name, newYear);
                        setBuyerStats(buyerData);
                      } catch (error) {
                        console.error('Error fetching buyer statistics:', error);
                      } finally {
                        setBuyerStatsLoading(false);
                      }
                    }}>&gt;</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;